import { useEffect, useRef, useState } from 'react';
import { useFormContext } from '../context/FormContext';

export function Recaptcha({
  recaptchaId = 0,
  onWidgetRendered,
  showError = false,
}: {
  recaptchaId?: number;
  /**
   * Reports the id grecaptcha assigned to this widget. The caller has to validate that
   * id rather than assume one: this component is unmounted whenever the user leaves the
   * final step, and grecaptcha hands out a new id each time it is rendered again.
   */
  onWidgetRendered?: (widgetId: number) => void;
  showError?: boolean;
}) {
  const { recaptchaService } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [expired, setExpired] = useState<boolean>(false);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    const renderWidget = () => {
      if (!containerRef.current || !window.grecaptcha) {
        return;
      }

      // Only render once per mount — the ref survives the effect re-running (React
      // StrictMode does so deliberately) and grecaptcha rejects a second render into
      // the same element. Registration below is repeated so it survives the cleanup.
      if (widgetIdRef.current === null) {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: recaptchaService.getCaptchaKey(),
          callback: () => setExpired(false),
          'expired-callback': () => setExpired(true),
        });
      }

      recaptchaService.addRecaptchaId(widgetIdRef.current);

      if (onWidgetRendered) {
        onWidgetRendered(widgetIdRef.current);
      }
    };

    if (window.grecaptcha) {
      renderWidget();
    } else {
      window.onRecaptchaLoad = renderWidget;
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current !== null) {
        recaptchaService.removeRecaptchaId(widgetIdRef.current);
      }
    };
  }, [recaptchaService, onWidgetRendered]);

  const message = expired || showError;

  return (
    <div className="_recaptcha">
      <div ref={containerRef} data-recaptcha-id={recaptchaId} />
      <div
        className={`validation-msg${message ? ' active' : ''}`}
        style={{ display: message ? undefined : 'none' }}
      >
        Please complete the reCAPTCHA
      </div>
    </div>
  );
}
